import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import { 
  Calendar,  
  Search,
  CalendarRange,
  RefreshCw
} from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';
import { useAuth } from '../contexts/AuthContext';

const parseLocalDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

// Data legível no cabeçalho da folha impressa (ex: "Mon, Jul 21, 2026").
const formatPrintDate = (dateString) => {
  const d = parseLocalDate(dateString);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
  });
};

const PayoutsPage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refreshKey } = useRefresh();
  const [dateMode, setDateMode] = useState(0); 
  
  const [paymentDates, setPaymentDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null
  });
  
  const [calculationPeriod, setCalculationPeriod] = useState({
    startDate: '',
    endDate: ''
  });
  
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [openModal, setOpenModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const fetchEmployeesData = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      const employeesRes = await api.get('/employees');
      const activeEmployees = employeesRes.data.filter((emp) => emp[6] === 'active');
      
      const employeesWithSalary = await Promise.all(
        activeEmployees.map(async (emp) => {
          try {
            const salaryRes = await api.get(`/salary/${emp[0]}?start_date=${startDate}&end_date=${endDate}`);
            return {
              id: emp[0],
              name: emp[1],
              role: emp[3] === 3 ? 'Admin' : emp[3] === 2 ? 'Manager' : 'Employee',
              hourlyRate: emp[4],
              commissionPercent: emp[5],
              salary: salaryRes.data
            };
          } catch (error) {
            console.error(`Error fetching salary for employee ${emp[0]}:`, error);
            return {
              id: emp[0],
              name: emp[1],
              role: emp[3] === 3 ? 'Admin' : emp[3] === 2 ? 'Manager' : 'Employee',
              hourlyRate: emp[4],
              commissionPercent: emp[5],
              salary: { total: 0 }
            };
          }
        })
      );
      
      setEmployees(employeesWithSalary);
      setFilteredEmployees(employeesWithSalary);
      setSearchQuery('');
    } catch (error) {
      console.error('Error fetching employees data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const calculatePeriod = useCallback((paymentDateString) => {
    const localPaymentDate = parseLocalDate(paymentDateString);
    if (!localPaymentDate) return;

    const startDate = new Date(localPaymentDate);
    startDate.setDate(localPaymentDate.getDate() - 14); 
    
    const endDate = new Date(localPaymentDate);
    endDate.setDate(localPaymentDate.getDate() - 8); 
    
    const startDateStr = format(startDate, 'yyyy-MM-dd');
    const endDateStr = format(endDate, 'yyyy-MM-dd');

    setCalculationPeriod({
      startDate: startDateStr,
      endDate: endDateStr
    });
    
    fetchEmployeesData(startDateStr, endDateStr);
  }, [fetchEmployeesData]);

  const applyCustomDateRange = useCallback(() => {
    if (!customDateRange.startDate || !customDateRange.endDate) {
      return;
    }
    
    const startDateStr = format(customDateRange.startDate, 'yyyy-MM-dd');
    const endDateStr = format(customDateRange.endDate, 'yyyy-MM-dd');
    
    setCalculationPeriod({
      startDate: startDateStr,
      endDate: endDateStr
    });
    
    fetchEmployeesData(startDateStr, endDateStr);
  }, [customDateRange.startDate, customDateRange.endDate, fetchEmployeesData]);
  
  useEffect(() => {
    const generatePaymentDates = () => {
      const dates = [];
      const today = new Date();
      
      const currentMonday = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; 
      currentMonday.setDate(today.getDate() + diff);
      currentMonday.setHours(0, 0, 0, 0);
      
      for (let i = -5; i < 6; i++) {
        const date = new Date(currentMonday);
        date.setDate(currentMonday.getDate() + (i * 7));
        
        dates.push({
          value: format(date, 'yyyy-MM-dd'),
          label: new Date(date).toLocaleDateString('en-US', { 
            month: '2-digit', 
            day: '2-digit', 
            year: 'numeric' 
          })
        });
      }
      
      setPaymentDates(dates);
      const defaultDateValue = format(currentMonday, 'yyyy-MM-dd');
      const defaultDate = dates.find(d => d.value === defaultDateValue) || dates[5]; 
      
      if (defaultDate) {
        setSelectedDate(defaultDate.value);
        calculatePeriod(defaultDate.value);
      }
    };
    
    generatePaymentDates();
  }, [calculatePeriod]);
  
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEmployees(employees);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = employees.filter(emp => 
      emp.name.toLowerCase().includes(query)
    );
    
    setFilteredEmployees(filtered);
  }, [searchQuery, employees]);

  useEffect(() => {
    if (dateMode === 0 && selectedDate) {
      calculatePeriod(selectedDate);
    } else if (dateMode === 1 && customDateRange.startDate && customDateRange.endDate) {
      applyCustomDateRange();
    }
  }, [refreshKey, dateMode, selectedDate, customDateRange.startDate, customDateRange.endDate, calculatePeriod, applyCustomDateRange]);
  
  const handleDateChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedDate(selectedValue);
    calculatePeriod(selectedValue);
  };
  
  const handleCustomDateChange = (type, newDate) => {
    setCustomDateRange(prev => ({
      ...prev,
      [type]: newDate ? new Date(newDate.setHours(0,0,0,0)) : null
    }));
  };
    
  const handleDateModeChange = (event, newValue) => {
    setDateMode(newValue);
    
    if (newValue === 0 && selectedDate) {
      calculatePeriod(selectedDate);
    }
    else if (newValue === 1 && customDateRange.startDate && customDateRange.endDate) {
      applyCustomDateRange();
    }
  };
  
  const handleOpenDetails = async (employee) => {
    setSelectedEmployee(employee);
    setOpenModal(true);
    setLoadingDetails(true);
    
    try {
      const detailsRes = await api.get(`/salary/${employee.id}/details`, {
        params: {
          start_date: calculationPeriod.startDate,
          end_date: calculationPeriod.endDate
        }
      });
      
      setEmployeeDetails(detailsRes.data);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      const mockDailyData = generateMockDailyData(employee, calculationPeriod);
      setEmployeeDetails(mockDailyData);
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const generateMockDailyData = (employee, period) => {
    const { startDate, endDate } = period;
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    const dailyData = [];
    
    if (!start || !end) return { employee: employee.name, period, dailyData: [] };

    const currentDate = new Date(start);
    while (currentDate <= end) {
      const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
      
      if (dayOfWeek !== 'Saturday' && dayOfWeek !== 'Sunday') {
        dailyData.push({
          date: format(currentDate, 'yyyy-MM-dd'),
          dayOfWeek,
          clockIn: null,
          clockOut: null,
          salesSubtotal: Math.random() * 400,
          charmsQty: Math.round(Math.random() * 8),
          hours: Math.random() * 8,
          hourlyTotal: Math.random() * 100,
          commissionBase: Math.random() * 200,
          commission: Math.random() * 50,
          charmBonus: Math.random() * 20,
          tips: Math.random() * 30,
          total: Math.random() * 200
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return {
      employee: employee.name,
      period: {
        startDate,
        endDate
      },
      dailyData
    };
  };
  
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEmployee(null);
    setEmployeeDetails(null);
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const calculateAverageHourlyRate = (salary) => {
    if (!salary || !salary.hours || salary.hours === 0) {
      return 0;
    }
    const totalEarnings = (salary.hourly_total || 0) + 
                          (salary.commission || 0) + 
                          (salary.charm_bonus_total || 0) + 
                          (salary.tip || 0);
    return totalEarnings / salary.hours;
  };

  const calculateDailyAverageHourlyRate = (day) => {
    if (!day || !day.hours || day.hours === 0) {
        return 0;
    }
    const totalEarnings = (day.hourlyTotal || 0) +
                          (day.commission || 0) +
                          (day.charmBonus || 0) +
                          (day.tips || 0);
    return totalEarnings / day.hours;
  };
  
  const calculateDailyTotals = (dailyData) => {
    const sum = (pick) => dailyData.reduce((acc, day) => acc + (pick(day) || 0), 0);
    return {
      salesSubtotal: sum((d) => d.salesSubtotal),
      charmsQty: sum((d) => (Number.isFinite(d.charmsQty) ? d.charmsQty : 0)),
      hours: sum((d) => d.hours),
      hourlyTotal: sum((d) => d.hourlyTotal),
      commission: sum((d) => d.commission),
      charmBonus: sum((d) => d.charmBonus),
      tips: sum((d) => d.tips),
      total: sum((d) => d.total)
    };
  };

  const calculateTotals = () => {
    const sum = (pick) => filteredEmployees.reduce((acc, emp) => acc + (pick(emp.salary || {}) || 0), 0);
    const t = {
      count: filteredEmployees?.length || 0,
      hours: 0,
      hourly_total: 0,
      commission: 0,
      charm_bonus_total: 0,
      tip: 0,
      total: 0
    };
    if (!t.count) return t;

    t.hours = sum((s) => s.hours);
    t.hourly_total = sum((s) => s.hourly_total);
    t.commission = sum((s) => s.commission);
    t.charm_bonus_total = sum((s) => s.charm_bonus_total);
    t.tip = sum((s) => s.tip);
    t.total = sum((s) => s.total);
    return t;
  };
  
  const totals = calculateTotals();

  if (user?.role_id !== 3) {
    return (
      <Box p={2}>
        <Typography variant="h6" color="error">
          Access Denied: Only Admin can view this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      mt: -4,
      pt: -4,
      p: 1, 
      maxWidth: '1000px', 
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}>   
      <Paper 
        elevation={0} 
        className="print-hide"
        sx={{ 
          pt: -4,
          px: 1,
          pb: 1,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Tabs 
          value={dateMode} 
          onChange={handleDateModeChange}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ mb: 2 }}
        >
          <Tab 
            icon={<Calendar size={18} />} 
            label="Monday Payments" 
            iconPosition="start"
          />
          <Tab 
            icon={<CalendarRange size={18} />} 
            label="Custom Date Range" 
            iconPosition="start"
          />
        </Tabs>
        
        {dateMode === 0 ? (
          <Grid container spacing={2} alignItems="center" width='100%'>
            <Grid item xs={12} width='100%'>
              <FormControl fullWidth>
                <InputLabel id="payment-date-label">Payment Date (Monday)</InputLabel>
                <Select
                  labelId="payment-date-label"
                  id="payment-date"
                  value={selectedDate}
                  label="Payment Date (Monday)"
                  onChange={handleDateChange}
                  startAdornment={<Calendar size={18} style={{ marginRight: '8px' }} />}
                  sx={{ textAlign: 'center' }}
                >
                  {paymentDates.map((date) => (
                    <MenuItem key={date.value} value={date.value}>
                      {date.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} alignItems="center" justifyContent="center">
              <Grid item xs={12} sm={5}>
                <DatePicker
                  label="Start Date"
                  value={customDateRange.startDate}
                  onChange={(newDate) => handleCustomDateChange('startDate', newDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <DatePicker
                  label="End Date"
                  value={customDateRange.endDate}
                  onChange={(newDate) => handleCustomDateChange('endDate', newDate)}
                  slotProps={{ textField: { fullWidth: true } }}
                  minDate={customDateRange.startDate}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={applyCustomDateRange}
                  disabled={!customDateRange.startDate || !customDateRange.endDate}
                  sx={{ height: '56px', width: '250px' }} 
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
        )}
        
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Card sx={{ 
            borderRadius: 2, 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0',
            width: '100%'
          }}>
            <CardContent sx={{ 
              p: 1, 
              '&:last-child': { pb: 2 },
              textAlign: 'center'
            }}>
              <Typography variant="subtitle2" color="text.secondary">
                Calculation Period:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {calculationPeriod.startDate && calculationPeriod.endDate ? (
                  <>
                    {parseLocalDate(calculationPeriod.startDate)?.toLocaleDateString('en-US', { 
                      month: '2-digit', 
                      day: '2-digit', 
                      year: 'numeric' 
                    })}
                    {' to '}
                    {parseLocalDate(calculationPeriod.endDate)?.toLocaleDateString('en-US', { 
                      month: '2-digit', 
                      day: '2-digit', 
                      year: 'numeric' 
                    })}
                  </>
                ) : (
                  'Select a date range'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Paper>
      
      <Box maxWidth={'1000px'} className="print-hide">
        <TextField
          size='small'
          fullWidth
          variant="outlined"
          placeholder="Search employees..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={() => setSearchQuery('')}
                  edge="end"
                >
                  <RefreshCw size={16} />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ 
            width: { 100: '100%' },
            bgcolor: 'white',
            borderRadius: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />
      </Box>

      <Paper
        className="printable-content"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          backgroundColor: '#fff'
        }}
      >
        {/* Cabeçalho só do papel impresso. Os seletores de data ficam fora do
            .printable-content, então a folha saía sem dizer de que período era. */}
        <Box
          sx={{
            display: 'none',
            '@media print': { display: 'block', mb: 1, px: 1 },
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>PAYOUT</Typography>
          <Typography sx={{ fontSize: 11 }}>
            Period worked: {formatPrintDate(calculationPeriod.startDate)} to {formatPrintDate(calculationPeriod.endDate)}
          </Typography>
          {dateMode === 0 && selectedDate && (
            <Typography sx={{ fontSize: 11 }}>
              Payment date: {formatPrintDate(selectedDate)}
            </Typography>
          )}
          <Typography sx={{ fontSize: 11 }}>
            Printed: {new Date().toLocaleString('en-US')}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell align="right"><strong>Hours</strong></TableCell>
                  <TableCell align="right"><strong>Hourly Pay</strong></TableCell>
                  <TableCell align="right"><strong>Commission</strong></TableCell>
                  <TableCell align="right"><strong>Bonus</strong></TableCell>
                  <TableCell align="right"><strong>Tips</strong></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><strong>Avg Rate</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {searchQuery ? 'No employees found matching your search' : 'No employees found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => (
                    <TableRow 
                      key={employee.id}
                      hover
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                          cursor: 'pointer'
                        },
                        '@media print': {
                          '&:hover': {
                            bgcolor: 'transparent',
                            cursor: 'default'
                          }
                        }
                      }}
                      onClick={() => handleOpenDetails(employee)}
                    >
                      <TableCell>{employee.name}</TableCell>
                      <TableCell align="right">{employee.salary?.hours?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right">{formatCurrency(employee.salary?.hourly_total || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(employee.salary?.commission || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(employee.salary?.charm_bonus_total || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(employee.salary?.tip || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(calculateAverageHourlyRate(employee.salary))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(employee.salary?.total || 0)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: '#f5f5f5', '& td': { fontWeight: 'bold', color: 'text.primary' } }}>
                  <TableCell>Total ({totals.count} employees):</TableCell>
                  <TableCell align="right">{totals.hours.toFixed(2)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.hourly_total)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.commission)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.charm_bonus_total)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.tip)}</TableCell>
                  {/* ponytail: media ponderada (ganhos totais / horas totais), nao media das medias */}
                  <TableCell align="right">{formatCurrency(calculateAverageHourlyRate(totals))}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.total)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Paper>
      
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {selectedEmployee?.name} - {employeeDetails ? 
            `${parseLocalDate(employeeDetails.period.startDate)?.toLocaleDateString()} to ${parseLocalDate(employeeDetails.period.endDate)?.toLocaleDateString()}` 
            : 'Payment Details'}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : employeeDetails ? (
            <Box>
              <TableContainer 
                component={Paper} 
                variant="outlined" 
                sx={{ 
                  mt: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  borderRadius: 2
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Day</TableCell>
                      <TableCell>Clock In</TableCell>
                      <TableCell>Clock Out</TableCell>
                      <TableCell align="right">Sales</TableCell>
                      <TableCell align="right">Charms</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right">Hourly Pay</TableCell>
                      <TableCell align="right">Commission</TableCell>
                      <TableCell align="right">Bonus</TableCell>
                      <TableCell align="right">Tips</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Avg Rate</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeDetails.dailyData.map((day) => (
                      <TableRow key={day.date} hover>
                        <TableCell>{parseLocalDate(day.date)?.toLocaleDateString()}</TableCell>
                        <TableCell>{day.dayOfWeek}</TableCell>
                        <TableCell>{day.clockIn || '-'}</TableCell>
                        <TableCell>{day.clockOut || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(day.salesSubtotal || 0)}</TableCell>
                        <TableCell align="right">{Number.isFinite(day.charmsQty) ? day.charmsQty : 0}</TableCell>
                        <TableCell align="right">{(day.hours ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.hourlyTotal || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.commission || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.charmBonus || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(day.tips || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(calculateDailyAverageHourlyRate(day))}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(day.total || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    {(() => {
                      const dt = calculateDailyTotals(employeeDetails.dailyData);
                      return (
                        <TableRow sx={{ bgcolor: '#f5f5f5', '& td': { fontWeight: 'bold', color: 'text.primary' } }}>
                          <TableCell colSpan={4}>Week Total:</TableCell>
                          <TableCell align="right">{formatCurrency(dt.salesSubtotal)}</TableCell>
                          <TableCell align="right">{dt.charmsQty}</TableCell>
                          <TableCell align="right">{dt.hours.toFixed(2)}</TableCell>
                          <TableCell align="right">{formatCurrency(dt.hourlyTotal)}</TableCell>
                          <TableCell align="right">{formatCurrency(dt.commission)}</TableCell>
                          <TableCell align="right">{formatCurrency(dt.charmBonus)}</TableCell>
                          <TableCell align="right">{formatCurrency(dt.tips)}</TableCell>
                          {/* ponytail: media ponderada, mesmo criterio do rodape da tabela principal */}
                          <TableCell align="right">{formatCurrency(calculateDailyAverageHourlyRate(dt))}</TableCell>
                          <TableCell align="right">{formatCurrency(dt.total)}</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableFooter>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutsPage;
